import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Loader2, Search, RefreshCw, Copy, CheckCircle, RotateCcw, Plus } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { TableSortHeader, useTableSort } from "./TableSortHeader";
import { StatusFilter } from "./StatusFilter";

interface Token {
  id: string;
  code: string;
  status: string;
  purchase_email: string;
  purchase_id: string;
  redeemed_by_email: string | null;
  redeemed_at: string | null;
  completed_at: string | null;
  created_at: string;
}

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "available", label: "Available" },
  { value: "in_use", label: "In Use" },
  { value: "used", label: "Used" },
];

export const AdminTokensTab = () => {
  const [tokens, setTokens] = useState<Token[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [resettingTokenId, setResettingTokenId] = useState<string | null>(null);
  const [generatingToken, setGeneratingToken] = useState(false);
  const { toast } = useToast();
  const { sortKey, sortDirection, handleSort, sortData } = useTableSort<Token>("created_at", "desc");

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('access_tokens')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTokens(data || []);
    } catch (error) {
      toast({
        title: "Error loading data",
        description: "Failed to load tokens. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
      toast({
        title: "Copied",
        description: "Token code copied to clipboard.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy code.",
        variant: "destructive",
      });
    }
  };

  const resetToken = async (tokenId: string) => {
    setResettingTokenId(tokenId);
    try {
      const { data, error } = await supabase.rpc('reset_token_for_admin', {
        p_token_id: tokenId
      });

      if (error) throw error;
      
      if (data) {
        toast({
          title: "Token reset",
          description: "The token has been reset and is now available for reuse.",
        });
        fetchData();
      } else {
        toast({
          title: "Reset failed",
          description: "Could not reset the token. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reset token.",
        variant: "destructive",
      });
    } finally {
      setResettingTokenId(null);
    }
  };

  const generateTestToken = async () => {
    setGeneratingToken(true);
    try {
      const { data: newCode, error } = await supabase.rpc('generate_test_token_for_admin');

      if (error) throw error;

      toast({
        title: "Test token created",
        description: `Created token ${newCode}`,
      });
      fetchData();
    } catch (error) {
      console.error('Error generating test token:', error);
      toast({
        title: "Error",
        description: "Failed to generate test token.",
        variant: "destructive",
      });
    } finally {
      setGeneratingToken(false);
    }
  };

  const filteredTokens = useMemo(() => {
    let result = tokens;

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(token =>
        token.code.toLowerCase().includes(searchLower) ||
        token.purchase_email.toLowerCase().includes(searchLower) ||
        token.redeemed_by_email?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      result = result.filter(token => token.status === statusFilter);
    }

    return result;
  }, [tokens, searchTerm, statusFilter]);

  const sortedTokens = useMemo(() => sortData(filteredTokens), [filteredTokens, sortKey, sortDirection]);

  const availableCount = tokens.filter(t => t.status === 'available').length;
  const usedCount = tokens.filter(t => t.status === 'used').length;
  const inUseCount = tokens.filter(t => t.status === 'in_use').length;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'available':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Available</Badge>;
      case 'in_use':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">In Use</Badge>;
      case 'used':
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">Used</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle>Access Tokens</CardTitle>
            <CardDescription>
              {availableCount} available, {inUseCount} in use, {usedCount} used
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search tokens..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-48"
              />
            </div>
            <StatusFilter
              value={statusFilter}
              onValueChange={setStatusFilter}
              options={STATUS_OPTIONS}
            />
            <Button variant="outline" size="icon" onClick={fetchData}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button onClick={generateTestToken} disabled={generatingToken}>
              {generatingToken ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              New Test Token
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : sortedTokens.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            {searchTerm || statusFilter !== "all" ? "No results match your filters" : "No tokens found"}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <TableSortHeader
                      label="Code"
                      sortKey="code"
                      currentSortKey={sortKey}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead>
                    <TableSortHeader
                      label="Status"
                      sortKey="status"
                      currentSortKey={sortKey}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead>
                    <TableSortHeader
                      label="Purchased By"
                      sortKey="purchase_email"
                      currentSortKey={sortKey}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead>
                    <TableSortHeader
                      label="Redeemed By"
                      sortKey="redeemed_by_email"
                      currentSortKey={sortKey}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead>
                    <TableSortHeader
                      label="Created"
                      sortKey="created_at"
                      currentSortKey={sortKey}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead>
                    <TableSortHeader
                      label="Used"
                      sortKey="completed_at"
                      currentSortKey={sortKey}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedTokens.map((token) => (
                  <TableRow key={token.id}>
                    <TableCell className="font-mono font-medium">{token.code}</TableCell>
                    <TableCell>{getStatusBadge(token.status)}</TableCell>
                    <TableCell>{token.purchase_email}</TableCell>
                    <TableCell>{token.redeemed_by_email || "—"}</TableCell>
                    <TableCell>
                      {format(new Date(token.created_at), "dd MMM yyyy")}
                    </TableCell>
                    <TableCell>
                      {token.completed_at 
                        ? format(new Date(token.completed_at), "dd MMM yyyy")
                        : "—"
                      }
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => copyCode(token.code)}
                        >
                          {copiedCode === token.code ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                        {(token.status === 'in_use' || token.status === 'used') && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                disabled={resettingTokenId === token.id}
                              >
                                {resettingTokenId === token.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <RotateCcw className="h-4 w-4" />
                                )}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Reset Token?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will reset the token <strong>{token.code}</strong> to "available" status, 
                                  clearing any associated assessment data. The token can then be reused by another user.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => resetToken(token.id)}>
                                  Reset Token
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
